// eslint-disable-next-line no-unused-vars
import React, { memo, useState, useEffect, useMemo } from 'react';
import { stringify } from 'query-string';
import { useTranslation } from 'react-i18next';
import omit from 'lodash/omit';

import useFetch from 'Hooks/useFetch';
import useAsyncError from 'Hooks/useAsyncError';
import useRouter from 'Hooks/useRouter';
import useUser from 'Hooks/useUser';
import useAnalytics from 'Hooks/useAnalytics';

import ReservedProductsComponent from 'Components/ReservedProducts/ReservedProducts';
import { API_RESERVED_PRODUCTS } from 'Constants';

const defaultSortId = 'product-desc';

const ReservedProducts = memo(() => {
  const [reservedProducts, setReservedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  // const [errors, setErrors] = useState([]);

  const throwError = useAsyncError();
  const fetcher = useFetch();
  const { t } = useTranslation();
  const { location, query, push } = useRouter();
  const { data: { email, loggedIn } } = useUser();
  const analytics = useAnalytics();

  const sortOptions = useMemo(() => [
    { id: 'product-asc', label: `${t('pages.product')} ${t('orders.ascending')}` },
    { id: 'product-desc', label: `${t('pages.product')} ${t('orders.descending')}` },
    { id: 'quantity-asc', label: `${t('pages.quantity')} ${t('orders.ascending')}` },
    { id: 'quantity-desc', label: `${t('pages.quantity')} ${t('orders.descending')}` },
    { id: 'expiration_date-asc', label: `${t('pages.expirationDate')} ${t('orders.ascending')}` },
    { id: 'expiration_date-desc', label: `${t('pages.expirationDate')} ${t('orders.descending')}` },
    { id: 'order_number-asc', label: `${t('pages.orderNumber')} ${t('orders.ascending')}` },
    { id: 'order_number-desc', label: `${t('pages.orderNumber')} ${t('orders.descending')}` },
  ], []);

  const defaultSort = sortOptions[2];
  const currentSortId = query?.order_by
    ? `${ query.order_by }-${ query.order_direction }`
    : defaultSortId;
  const [currentSort, setSort] = useState(
    currentSortId !== defaultSort.id
      ? sortOptions.find(item => item.id === currentSortId)
      : defaultSort
  );

  useEffect(() => {
    // check if query has sort
    if (Object.keys(omit(query || {}, ['order_number', 'product_code', 'page'])).length) {
      const { order_by: orderBy, order_direction: orderDirection } = query;
      setSort(sortOptions.find(item => item.id === `${ orderBy }-${ orderDirection }`));
    }
  }, []);

  const onMobileOrderChange = sort => {
    const [orderBy, orderDirection] = sort.split('-');
    analytics.track('sort_reserved_products', { order_by: orderBy, order_direction: orderDirection });

    if (orderDirection === 'none' || sort === defaultSortId) {
      setSort(sortOptions.find(item => item.id === defaultSortId));
      push({
        location: location.pathname,
        search: stringify(omit(query || {}, ['order_by', 'order_direction']))
      });
      return;
    }

    setSort(sortOptions.find(item => item.id === sort));

    const queryParams = {
      ...(omit(query || {}, ['order_by', 'order_direction', 'page'])),
      order_by: orderBy,
      order_direction: orderDirection
    };

    push({
      location: location.pathname,
      search: stringify(queryParams)
    });
  };

  const getReservedProducts = () => {
    setLoading(true);

    let path = API_RESERVED_PRODUCTS;

    if (query) {
      path += `?${ stringify({ ...query }) }`;
    }

    fetcher(path).then(res => {
      if (res.length === 0) {
        setReservedProducts([]);
        setPagination(null);
        return;
      }

      const { items, ...resPagination } = res;

      const mappedReservedProducts = items.map(product => ({
        ...product,
        id: `${ product.orderNumber }-${ product.productId }`,
        productCode: {
          title: product?.title,
          slug: product?.slug,
          productId: product?.productId,
          erpProductId: product?.erpProductId,
          urlThumbnailPicture: product?.urlThumbnailPicture,
        },
        quantity: {
          quantity: product?.quantity,
          erpProductId: product?.erpProductId,
        },
        actions: product.orderId,
      }));

      setReservedProducts(mappedReservedProducts);
      setPagination(resPagination);
    }).catch(err => throwError(err))
      .finally(() => setLoading(false));
  };

  // Get Reserved Products on page load
  useEffect(() => {
    if (loggedIn) {
      getReservedProducts();
    }
  }, [location.pathname, query, email, loggedIn]);

  return (
    <ReservedProductsComponent
      reservedProducts={ reservedProducts }
      loading={ loading }
      pagination={ pagination }
      sortOptions={ sortOptions }
      defaultSort={ defaultSort }
      currentSort={ currentSort }
      handleMobileOrderChange={ onMobileOrderChange }
    />
  );
});

ReservedProducts.displayName = 'ReservedProductsContainer';

export default ReservedProducts;
